// frontend/src/pages/dashboard/accounts/TikTokModals.js
import React from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * TikTokModals holds:
 *   1) Connect TikTok Modal
 *   2) Edit/Delete (TikTok) Modal
 *   3) Confirm Delete Modal
 *
 * Props from Accounts.js:
 *
 *   // Connect TikTok
 *   showTikTokModal, setShowTikTokModal,
 *   tikTokAccounts, selectedTikTokIds,
 *   handleTikTokCheckboxChange, handleConnectSelectedTikTok,
 *
 *   // Edit/Delete
 *   showTikTokDeleteModal, setShowTikTokDeleteModal,
 *   showTikTokDeleteConfirmModal, setShowTikTokDeleteConfirmModal,
 *   handleDeleteTikTokAccounts, handleDeleteTikTokConfirm,
 *
 *   // Possibly "accounts" if referencing them
 *   accounts
 */
export default function TikTokModals({
  // Connect TikTok
  showTikTokModal,
  setShowTikTokModal,
  tikTokAccounts,
  selectedTikTokIds,
  handleTikTokCheckboxChange,
  handleConnectSelectedTikTok,

  // Edit/Delete
  showTikTokDeleteModal,
  setShowTikTokDeleteModal,
  showTikTokDeleteConfirmModal,
  setShowTikTokDeleteConfirmModal,
  handleDeleteTikTokAccounts,
  handleDeleteTikTokConfirm,

  // Possibly pass the entire accounts array if referencing them
  accounts,
}) {
  // If referencing the connected TikTok data from "accounts":
  const tkAcc = accounts
    ? accounts.find((acc) => acc.platform.toLowerCase() === 'tiktok')
    : null;

  return (
    <>
      {/* ******** CONNECT TIKTOK MODAL ******** */}
      <Modal show={showTikTokModal} onHide={() => setShowTikTokModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select TikTok Accounts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(!tikTokAccounts || tikTokAccounts.length === 0) ? (
            <p>No TikTok accounts found.</p>
          ) : (
            <ul className="list-group">
              {tikTokAccounts.map((acc) => {
                const tid = acc.tiktokId || acc.tkId;
                const checked = selectedTikTokIds.includes(tid);
                return (
                  <li key={tid} className="list-group-item d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleTikTokCheckboxChange(tid)}
                    />
                    {acc.profileImage && (
                      <img
                        src={acc.profileImage}
                        alt={acc.displayName}
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          marginRight: '8px',
                        }}
                      />
                    )}
                    <span>{acc.displayName}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTikTokModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConnectSelectedTikTok}>
            Connect
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ******** EDIT/DELETE TIKTOK MODAL ******** */}
      <Modal show={showTikTokDeleteModal} onHide={() => setShowTikTokDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete TikTok Accounts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            // If referencing the main "accounts" for connected data
            if (!tkAcc || !tkAcc.connectedPages || tkAcc.connectedPages.length === 0) {
              return <p>No connected TikTok accounts to delete.</p>;
            }
            return (
              <ul className="list-group">
                {tkAcc.connectedPages.map((item) => {
                  const tid = item.tiktokId;
                  const checked = selectedTikTokIds.includes(tid);
                  return (
                    <li key={tid} className="list-group-item d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={checked}
                        onChange={() => handleTikTokCheckboxChange(tid)}
                      />
                      {item.profileImage && (
                        <img
                          src={item.profileImage}
                          alt={item.displayName}
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            marginRight: '8px',
                          }}
                        />
                      )}
                      <span>{item.displayName}</span>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTikTokDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteTikTokAccounts}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ******** CONFIRM DELETE TIKTOK MODAL ******** */}
      <Modal
        show={showTikTokDeleteConfirmModal}
        onHide={() => setShowTikTokDeleteConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete these TikTok accounts?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTikTokDeleteConfirmModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={handleDeleteTikTokConfirm}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
